import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import type { WarehouseTask } from '@zilian/contracts';
import { accessoriesText, amount } from './ui';

/**
 * The picking sheet (备货单): what to take off the shelves for one order, one A4 page each, so a
 * morning's orders print in one go. Items and quantities only, never prices: it travels around
 * the warehouse. The code in the corner opens the order's ship page in WeChat, where the keeper
 * confirms what went out once the goods are packed.
 */
export interface PickingDoc {
  task: WarehouseTask;
  /** Image URL of the code that opens the ship page; null when it could not be made. */
  qr: string | null;
}

const day = (v: string | Date) => {
  const d = new Date(v);
  return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日`;
};

const cell: React.CSSProperties = { border: '1px solid #000', padding: '6px 8px' };

/** Prints the sheets: renders them (hidden on screen), waits for the codes, opens print. */
export function usePickingSheets(issuer: string) {
  const [docs, setDocs] = useState<PickingDoc[] | null>(null);
  useEffect(() => {
    if (!docs) return;
    document.body.classList.add('printing-sheet');
    let cancelled = false;
    const images = [...document.querySelectorAll<HTMLImageElement>('.print-sheet img')];
    Promise.all(images.map((img) => img.decode().catch(() => undefined))).then(() => {
      if (!cancelled) window.print();
    });
    const done = () => setDocs(null);
    window.addEventListener('afterprint', done);
    return () => {
      cancelled = true;
      window.removeEventListener('afterprint', done);
      document.body.classList.remove('printing-sheet');
    };
  }, [docs]);
  return {
    print: setDocs,
    sheets: docs
      ? createPortal(
          <>
            {docs.map((doc, i) => (
              <PickingSheet
                key={doc.task.id}
                {...doc}
                issuer={issuer}
                last={i === docs.length - 1}
              />
            ))}
          </>,
          document.body,
        )
      : null,
  };
}

function PickingSheet(p: PickingDoc & { issuer: string; last: boolean }) {
  const { task } = p;
  return (
    <div className="print-sheet" style={p.last ? undefined : { breakAfter: 'page' }}>
      <div style={{ position: 'relative', minHeight: '35mm', textAlign: 'center' }}>
        <div style={{ fontSize: '13pt', fontWeight: 700, paddingTop: '2mm' }}>{p.issuer}</div>
        <div style={{ fontSize: '22pt', fontWeight: 700, letterSpacing: '0.5em', marginTop: 8 }}>
          备货单
        </div>
        <div
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: '30mm',
            textAlign: 'center',
            fontSize: '8pt',
          }}
        >
          {p.qr ? (
            <>
              <img
                src={p.qr}
                alt=""
                style={{ width: '28mm', height: '28mm', display: 'block', margin: '0 auto' }}
              />
              <div style={{ marginTop: 3, fontWeight: 700 }}>微信扫码登记发货</div>
            </>
          ) : (
            <div style={{ border: '1px solid #000', padding: '7mm 2mm' }}>
              在电脑上“仓库作业”中登记发货
            </div>
          )}
        </div>
      </div>

      <table
        style={{ width: '100%', borderCollapse: 'collapse', marginTop: 10, fontSize: '10.5pt' }}
      >
        <tbody>
          <tr>
            <td style={{ ...cell, width: '13%' }}>客户</td>
            <td style={{ ...cell, width: '37%', fontWeight: 700 }}>{task.peer}</td>
            <td style={{ ...cell, width: '13%' }}>订单号</td>
            <td style={{ ...cell, width: '37%' }}>{task.number}</td>
          </tr>
          <tr>
            <td style={cell}>交货日期</td>
            <td style={{ ...cell, fontWeight: 700 }}>{day(task.deliveryDate)}</td>
            <td style={cell}>打印日期</td>
            <td style={cell}>{day(new Date())}</td>
          </tr>
          {task.note && (
            <tr>
              <td style={cell}>备注</td>
              <td style={cell} colSpan={3}>
                {task.note}
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <table
        style={{ width: '100%', borderCollapse: 'collapse', marginTop: 12, fontSize: '10.5pt' }}
      >
        <thead>
          <tr>
            {['序号', '商品名称', '规格', '单位', '本次应备', '实备'].map((h, i) => (
              <th
                key={h}
                style={{
                  ...cell,
                  fontWeight: 700,
                  textAlign: i >= 4 ? 'right' : i === 0 || i === 3 ? 'center' : 'left',
                  width: ['7%', '33%', '28%', '8%', '12%', '12%'][i],
                  whiteSpace: 'nowrap',
                }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {task.lines.map((l, i) => (
            <tr key={l.lineId}>
              <td style={{ ...cell, textAlign: 'center' }}>{i + 1}</td>
              <td style={cell}>
                {l.name}
                {l.accessories.length > 0 && (
                  <div style={{ fontSize: '8.5pt', marginTop: 2 }}>
                    随附：{accessoriesText(l.accessories, l.remaining)}
                  </div>
                )}
              </td>
              <td style={cell}>{l.spec}</td>
              <td style={{ ...cell, textAlign: 'center' }}>{l.unit}</td>
              <td style={{ ...cell, textAlign: 'right', fontWeight: 700 }}>
                {amount(l.remaining)}
                {Number(l.done) > 0 && (
                  <div style={{ fontSize: '8pt', fontWeight: 400 }}>
                    共 {amount(l.quantity)}，已发 {amount(l.done)}
                  </div>
                )}
              </td>
              {/* Left blank: the picker writes what was actually taken. */}
              <td style={cell} />
            </tr>
          ))}
        </tbody>
      </table>

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginTop: 28,
          fontSize: '10.5pt',
          gap: '6mm',
        }}
      >
        <span>备货人：________________</span>
        <span>复核人：________________</span>
        <span>备货日期：______月____日</span>
      </div>
      <div style={{ marginTop: 18, fontSize: '8pt', textAlign: 'right' }}>
        打印于 {new Date().toLocaleString('zh-CN', { hour12: false })} · 联仓通
      </div>
    </div>
  );
}
